import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles({
  root: {
    position: 'relative',
    display: 'flex',
    minHeight: 'auto',
    width: '100%',
    maxWidth: '100%',
    padding: 32,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 24,
    borderRadius: 12,
    border: '1px solid #E9E9E9',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)', // Safari
    background:
      'linear-gradient(189deg, rgba(210, 229, 255, 0.20) -21.45%, rgba(167, 221, 230, 0.20) 53.33%, rgba(172, 255, 251, 0.20) 130.35%), rgba(250, 250, 250, 0.40)',
    boxSizing: 'border-box',
    overflow: 'hidden',

    '@media (max-width: 640px)': {
      padding: 16,
      gap: 16,
      margin: '0',
      maxWidth: 'calc(100vw - 16px)'
    }
  },

  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    gap: 16
  },

  text: {
    color: '#1D1B20',
    fontFamily: 'Overused Grotesk',
    fontSize: 20,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '26px',
    textAlign: 'center',
    maxWidth: '100%',
    wordBreak: 'break-word'
  },

  image: {
    position: 'absolute',
    top: '-32px',
    left: '-30px',
    width: 64,
    height: 64,
    borderTopLeftRadius: 12,
    objectFit: 'cover'
  }
});

function BlurryCard({
  image,
  description,
  style,
  width,
  height,
  children,
  contentAlignment = 'center',
  className = ''
}) {
  const theme = useTheme();
  const classes = useStyles(theme);

  const dynamicStyle = {
    ...style,
    ...(width ? { width, maxWidth: width } : {}),
    ...(height ? { height } : {})
  };

  const getContentContainerClass = () => {
    const baseClass = classes.contentContainer;
    switch (contentAlignment) {
      case 'left':
        return `${baseClass} left-aligned`;
      case 'stretch':
        return `${baseClass} stretch`;
      default:
        return baseClass;
    }
  };

  let content;
  if (children) {
    content = <Box className={getContentContainerClass()}>{children}</Box>;
  } else if (image) {
    content = (
      <Box className={getContentContainerClass()}>
        <img src={image} alt="icon" className={classes.image} />
        <Typography className={classes.text}>{description}</Typography>
      </Box>
    );
  }

  return (
    <Box className={`${classes.root} ${className}`} style={dynamicStyle}>
      {content}
    </Box>
  );
}

BlurryCard.propTypes = {
  image: PropTypes.string,
  description: PropTypes.string,
  style: PropTypes.object,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node,
  contentAlignment: PropTypes.oneOf(['center', 'left', 'stretch']),
  className: PropTypes.string
};

export default BlurryCard;
